import React, { useContext, useEffect, useState } from "react";
import Button from "@common/components/Button";
import Dialog from "@common/components/Dialog";
import DialogActions from "@common/components/Dialog/DialogActions";
import DialogTitle from "@common/components/Dialog/DialogTitle";
import { DeviceModelAccountUpdate } from "@services/catalog/types";
import { useTranslation } from "react-i18next";
import { postModels, updateAccountDeviceModel } from "@services/catalog";
import { useErrorNotification } from "@common/components/Notification";
import DeviceInfos from "./DeviceInfos";
import DeviceTabs from "./DeviceTabs";
import { ReducerContext, ReducerDispatchContext } from "../context";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { canEditConfiguration } from "@pages/Configuration/Monitoring/common/utils";
import { hasExtendedPropertyMappingLicense } from "@common/utils/user";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";
import SlideUnsaved from "@common/components/SlideUnsaved";
import { useTheme } from "@mui/material/styles";

interface DeviceDialogProps {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}

const DeviceDialog = (props: DeviceDialogProps) => {
  const theme = useTheme();
  const {
    device,
    globalDevice,
    accountId,
    snmpEnabled,
    snmpEntries,
    adapterSelected,
    adapterChecked,
    propertyMappingEnabled,
    mappingEntries,
    mappingInherit,
    adapterInherit,
    errors,
    localMode,
    isDataChanged,
  } = useContext(ReducerContext);
  const [user] = useAuthenticatedUser();
  const canEdit = canEditConfiguration(user, undefined, accountId);
  const dispatch = useContext(ReducerDispatchContext);
  const { t } = useTranslation();
  const { open, onClose, refetch } = props;
  const dispatchError = useErrorNotification();
  const [, , allAccounts] = useUserSelectedAccounts();
  const [isWarningShown, setIsWarningShown] = useState<boolean>(false);

  const canAccessExtendedPropertyMapping = hasExtendedPropertyMappingLicense(
    allAccounts.find((a) => a.id === accountId)
  );

  useEffect(() => {
    dispatch({ type: "setErrors" });
  }, [mappingEntries, propertyMappingEnabled]);

  useEffect(() => {
    dispatch({ type: "openDialog", value: canEdit });
  }, [globalDevice]);

  const handleClose = (bRefetch?: boolean) => {
    dispatch({ type: "closeDialog" });
    if (bRefetch) refetch();
    onClose();
    setIsWarningShown(false);
  };

  const handleCloseWithWarning = () => {
    if (isDataChanged) {
      setIsWarningShown(true);
    } else {
      handleClose();
    }
  };

  const handleUpdateDevice = async () => {
    let mapping: { [key: string]: string } = {};
    if (propertyMappingEnabled) {
      mappingEntries.forEach((e) => {
        mapping[e.name ?? ""] = e.value ?? "";
      });
    }
    let data: DeviceModelAccountUpdate = {
      snmpEnabled: snmpEnabled,
      defaultDeviceAdapterId:
        adapterSelected && adapterChecked ? adapterSelected : null,
      propertyMappingEnabled: propertyMappingEnabled,
      mappedExtendedProperties: mapping,
    };

    if (accountId) {
      data = {
        ...data,
        overrideGlobalOids: localMode,
        overrideGlobalAdapter: !adapterInherit,
        overrideGlobalPropertyMapping: !mappingInherit,
        snmpEntries: snmpEntries,
        defaultDeviceAdapterId: !adapterInherit
          ? adapterSelected && adapterChecked
            ? adapterSelected
            : null
          : undefined,
      };

      try {
        await updateAccountDeviceModel(accountId, device?.id, data);
        handleClose(true);
      } catch (e) {
        dispatchError(e);
      }
    } else if (device.id) {
      try {
        await postModels(device.id, data);
        handleClose(true);
      } catch (e) {
        dispatchError(e);
      }
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleCloseWithWarning}
      slotProps={{
        paper: {
          elevation: 0,
          sx: { height: "calc(100% - 64px)" },
        },
      }}
    >
      <DialogTitle>{device.model}</DialogTitle>
      <DeviceInfos refetchDevices={props.refetch} />
      <DeviceTabs />
      <DialogActions>
        <Button
          onClick={() => handleUpdateDevice()}
          color="white"
          variant="contained"
          disabled={!canEdit || errors.mapping || errors.oid}
        >
          {t("Save")}
        </Button>
        <Button
          variant="outlined"
          color="greyed"
          onClick={() => handleClose(false)}
        >
          {t("Cancel")}
        </Button>
      </DialogActions>
      <SlideUnsaved
        direction="right"
        warning={isWarningShown}
        onClose={handleClose}
        onCancel={() => setIsWarningShown(false)}
        sx={{
          bottom: 0,
          padding: theme.spacing(2, 3),
        }}
      />
    </Dialog>
  );
};

export default DeviceDialog;
